import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import type { Disposition, DecisionKind, DecisionEnvelope } from '../types';
import { useAuth } from '../auth/AuthContext';
import DispositionBar, { dispositionLabel, deriveDecision, RECOMMENDED_DISPOSITION } from './DispositionBar';
import { formatLocalTime } from '../format';

const DECISION_LABEL: Record<DecisionKind, string> = { approve: 'Approve', modify: 'Modify', override: 'Override' };
const DECISION_HINT: Record<DecisionKind, string> = {
  approve: 'You are accepting the recommended move.',
  modify: 'You are choosing a different response than the one recommended.',
  override: 'This acts against a standing constraint, so it needs a reason and executive authority.',
};

interface Props {
  owner: string;
  recommended?: Disposition;
  /** Commit the decision once, on the explicit Record button. Returns the network promise. */
  commit: (disposition: Disposition, envelope: DecisionEnvelope) => Promise<unknown>;
  /** Optional hook for the "Open War Game" link shown after recording a Simulate. */
  onSimulate?: () => void;
  /** When the panel is already inside a card, skip the outer panel chrome. */
  embedded?: boolean;
}

/**
 * The one decision model (spec 11.4 Rule 3), shared by every disposition surface. On load nothing
 * is selected and nothing is logged. The six response cards are the only control; the recommended
 * card is badged, never pre-selected. The disposition (Approve / Modify / Override) is derived from
 * the chosen card and shown live. Override reveals a required reason and is gated on executive
 * authority. A single "Record decision" button commits exactly one entry reflecting the choice.
 */
export default function DecisionPanel({ owner, recommended = RECOMMENDED_DISPOSITION, commit, onSimulate, embedded = false }: Props) {
  const { user } = useAuth();
  const [selected, setSelected] = useState<Disposition | null>(null);
  const [reason, setReason] = useState('');
  const [recordedAt, setRecordedAt] = useState<string | null>(null);

  const decision: DecisionKind | null = selected ? deriveDecision(selected, recommended) : null;
  const isOverride = decision === 'override';
  const canAuthorize = user?.role === 'executive' || user?.role === 'superadmin';
  const needsReason = isOverride && reason.trim().length === 0;
  const blockedByAuthority = isOverride && !canAuthorize;

  const mutation = useMutation({
    mutationFn: () => {
      if (!selected || !decision) throw new Error('No response selected');
      const envelope: DecisionEnvelope = {
        decision,
        reason: decision === 'approve' ? null : reason.trim() || null,
        modified_params: null,
        decider: user?.email ?? owner,
      };
      return commit(selected, envelope);
    },
    onSuccess: () => setRecordedAt(new Date().toISOString()),
  });

  function pick(d: Disposition) {
    setSelected(d);
    setReason('');
    setRecordedAt(null);
    mutation.reset();
  }

  const canRecord = !!selected && !mutation.isPending && !recordedAt && !needsReason && !blockedByAuthority;

  return (
    <div className={embedded ? 'decision-embedded' : 'panel'} style={{ marginTop: embedded ? 12 : 14 }}>
      <div className="qhead"><span className="qdot" style={{ background: 'var(--amber)' }} />Decide from here</div>
      <div className="small" style={{ marginBottom: 4 }}>
        Logged to institutional memory, feeds the learning loop. Owner to notify: <b>{owner}</b>.
      </div>

      <DispositionBar
        selected={selected ?? undefined}
        recommended={recommended}
        disabled={mutation.isPending || !!recordedAt}
        onSelect={pick}
      />

      {selected && decision && !recordedAt && (
        <div className="derived-disposition">
          This records as <b>{DECISION_LABEL[decision]}</b>. <span className="faint">{DECISION_HINT[decision]}</span>
        </div>
      )}

      {isOverride && !recordedAt && (
        <input
          className="c2-input"
          placeholder="Reason for acting against the standing constraint (required)"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          style={{ width: '100%', marginTop: 10 }}
        />
      )}
      {blockedByAuthority && !recordedAt && (
        <div className="small" style={{ marginTop: 8, color: 'var(--red)' }}>
          An override must be recorded by an executive or higher. Choose Escalate to route this to someone who can decide.
        </div>
      )}

      {!recordedAt && (
        <button
          type="button"
          className="btn primary"
          style={{ marginTop: 12 }}
          disabled={!canRecord}
          onClick={() => mutation.mutate()}
        >
          {mutation.isPending ? 'Recording…' : 'Record decision'}
        </button>
      )}

      {mutation.isError && (
        <div className="small" style={{ marginTop: 10, color: 'var(--red)' }}>
          {mutation.error instanceof Error ? mutation.error.message : 'Could not record the decision.'}
        </div>
      )}

      {recordedAt && selected && decision && (
        <div className="small" style={{ marginTop: 12 }}>
          Recorded: <b style={{ color: 'var(--amber)' }}>{dispositionLabel(selected)}</b> ({DECISION_LABEL[decision].toLowerCase()})
          {decision !== 'approve' && reason ? <> · reason recorded</> : null}. Owner: <b>{owner}</b>. {formatLocalTime(recordedAt)}. Added to institutional memory.
          {selected === 'simulate' && onSimulate && (
            <> <a onClick={onSimulate} style={{ cursor: 'pointer' }}>Open War Game →</a></>
          )}
        </div>
      )}
    </div>
  );
}
